import { useEffect } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { AspectRatioIcon } from './AspectRatioIcon';
import {
  VideoSettings,
  RESOLUTIONS_FOR_ASPECT,
  VEO_ASPECT_RATIOS,
  DURATIONS,
  VeoAspectRatio,
  Resolution,
} from '../VideoGenerationOptions.types';
import { getVeoAllowedDurations, normaliseVeoSettings } from '../videoOptionsHelpers';
import { useTranslation } from 'react-i18next';

export type VeoVideoOptions = Extract<
  VideoSettings,
  {
    model: 'veo-3.1' | 'veo-3.1-fast';
  }
>;

interface VeoOptionsProps {
  value: VeoVideoOptions;
  onChange: (value: VideoSettings) => void;
  disabled?: boolean;
  /** Whether the turn carries image attachments, which veo consumes as reference images. */
  hasReferenceImages?: boolean;
}

/**
 * Renders the chat options for Veo video generation models (monks-ui ToggleGroup layout).
 */
export const VeoGenerationOptions = ({
  value: storedValue,
  onChange,
  disabled,
  hasReferenceImages,
}: VeoOptionsProps) => {
  const { t } = useTranslation('chats');

  const value = normaliseVeoSettings(storedValue, { hasReferenceImages });

  useEffect(() => {
    if (value !== storedValue) {
      onChange(value);
    }
  }, [value, storedValue, onChange]);

  const allowedResolutions = RESOLUTIONS_FOR_ASPECT[value.aspectRatio];
  const allowedDurations = getVeoAllowedDurations({
    resolution: value.resolution,
    hasReferenceImages,
    endFrame: value.endFrame,
  });
  const isDurationConstrained = allowedDurations.length < DURATIONS.length;

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.veoOptions.aspectRatio')}
        </span>
        <ToggleGroup
          type="single"
          value={value.aspectRatio}
          onValueChange={(next) => {
            if (next) onChange({ ...value, aspectRatio: next as VeoAspectRatio });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-2 w-full"
          disabled={disabled}
        >
          {VEO_ASPECT_RATIOS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className="flex flex-col gap-1 h-auto py-2"
            >
              <AspectRatioIcon orientation={option.orientation} />
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.veoOptions.resolution')}
        </span>
        <ToggleGroup
          type="single"
          value={value.resolution}
          onValueChange={(next) => {
            if (next) onChange({ ...value, resolution: next as Resolution });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-2 w-full"
          disabled={disabled}
        >
          {allowedResolutions.map((resolution) => (
            <ToggleGroupItem key={resolution} value={resolution} className="py-2">
              <span className="text-xs">{resolution}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {value.aspectRatio === '9:16' && (
          <span className="text-xs text-muted-foreground">
            {t('chats.veoOptions.n1080pIs169Only')}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-foreground-subtle">
          {t('chats.veoOptions.duration')}
        </span>
        <ToggleGroup
          type="single"
          value={String(value.durationSeconds)}
          onValueChange={(next) => {
            if (next) onChange({ ...value, durationSeconds: Number(next) });
          }}
          variant="outline"
          size="sm"
          className="grid grid-cols-3 w-full"
          disabled={disabled}
        >
          {DURATIONS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={String(option.value)}
              className="py-2"
              disabled={!allowedDurations.some((allowed) => allowed.value === option.value)}
            >
              <span className="text-xs">{option.label}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {isDurationConstrained && (
          <span className="text-xs text-muted-foreground">
            {value.endFrame
              ? t('chats.veoOptions.endFrameRenders8sOnly')
              : hasReferenceImages
                ? t('chats.veoOptions.attachedImagesRender8sOnly')
                : t('chats.veoOptions.n1080pRenders8sOnly')}
          </span>
        )}
      </div>
    </>
  );
};
